import { farmSubdivisionsQueryOptions } from '../../../hooks/data/useFarmSubdivisions'
import { queryClient } from '../../../queryClient'
import type { Farm, FarmSubdivision, MetricEvent } from '../../../types/apiModels'
import { fieldUsages } from '../fieldUsages'
import { DCNamespaces } from '../namespaces'
import { getMetricEvents } from './getMetricEvents'
import { joinMessages } from './mergeNamespaceStatus'
import type { NamespaceValidationStatus } from './unusedPaddocks'

const GRAZING_REQUIRED_USAGES: readonly number[] = [fieldUsages.Grazing, fieldUsages.Silvopastoral]

const FETCH_ERROR_MESSAGE =
  'No se pudo verificar el pastoreo de los lotes ganaderos. Intente nuevamente.'

const requiredMessage = (count: number) =>
  count === 1
    ? 'Hay 1 lote con uso Ganadero o Silvopastoril sin pastoreo asignado.'
    : `Hay ${count} lotes con uso Ganadero o Silvopastoril sin pastoreo asignado.`

const mixedReminder = (count: number) =>
  count === 1
    ? 'Hay 1 lote mixto sin pastoreo asignado. Cárguelo si hubo pastoreo este año.'
    : `Hay ${count} lotes mixtos sin pastoreo asignado. Cárguelos si hubo pastoreo este año.`

const paddockIdsOf = (rows: { paddockIds?: string[] | null }[]) =>
  new Set(rows.flatMap(row => row.paddockIds ?? []))

// Strict, like the API's send block: coercing would count paddocks the API lets through.
const fieldUsageOf = (event: MetricEvent): number | undefined => {
  const fieldUsageId = event.data?.fieldUsageId
  return typeof fieldUsageId === 'number' ? fieldUsageId : undefined
}

export async function livestockPaddocksWithoutGrazing(
  farm: Farm,
  year: string,
): Promise<NamespaceValidationStatus> {
  let usageEvents: MetricEvent[]
  let grazingEvents: MetricEvent[]
  let subdivisions: FarmSubdivision[]
  try {
    const [usage, grazing, yearSubdivisions] = await Promise.all([
      getMetricEvents({ farmId: farm.id, namespace: DCNamespaces.paddocks, year }),
      getMetricEvents({ farmId: farm.id, namespace: DCNamespaces.grazing, year }),
      // fetchQuery: ensureQueryData would keep serving the entry a loteo edit invalidated.
      queryClient.fetchQuery(farmSubdivisionsQueryOptions(farm.id, year)),
    ])
    usageEvents = usage ?? []
    grazingEvents = grazing ?? []
    subdivisions = yearSubdivisions ?? []
  } catch {
    return { disabled: false, totalError: 0, error: FETCH_ERROR_MESSAGE }
  }

  // A paddock removed from the loteo keeps its usage event; the API's send block skips it, so the
  // badge must too or the count would disagree with the rejection.
  const loteoPaddocks = paddockIdsOf(subdivisions)
  const loteoPaddocksWithUsage = (usages: readonly number[]) => {
    const declared = paddockIdsOf(usageEvents.filter(event => usages.includes(fieldUsageOf(event))))
    return new Set([...declared].filter(id => loteoPaddocks.has(id)))
  }

  const grazedPaddocks = paddockIdsOf(grazingEvents)
  const requiredPaddocks = loteoPaddocksWithUsage(GRAZING_REQUIRED_USAGES)
  const mixedPaddocks = loteoPaddocksWithUsage([fieldUsages.Mixed])

  const requiredWithoutGrazing = [...requiredPaddocks].filter(id => !grazedPaddocks.has(id)).length
  const mixedWithoutGrazing = [...mixedPaddocks].filter(
    id => !requiredPaddocks.has(id) && !grazedPaddocks.has(id),
  ).length

  return {
    disabled: false,
    totalError: requiredWithoutGrazing,
    error: joinMessages([
      requiredWithoutGrazing ? requiredMessage(requiredWithoutGrazing) : null,
      mixedWithoutGrazing ? mixedReminder(mixedWithoutGrazing) : null,
    ]),
  }
}
